/**
 * The cloud-production and device-secret routes of the route table
 * (`routes.ts` spreads these into `ROUTES`): PLAN §9.2's launch flow, §9.4's
 * device secrets, §9.6's metering, and the two signed routes a cloud
 * production's task calls (its assignment, and "ready").
 *
 * Cloud only, like the sites table. Permissions are checked on the resource
 * (D14): a cloud production and a device secret are their studio's.
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import {
  cloudAssignmentResponseWireSchema,
  cloudProductionPathWireSchema,
  cloudProductionResponseWireSchema,
  cloudReadyRequestWireSchema,
  cloudUsageQueryWireSchema,
  cloudUsageResponseWireSchema,
  launchCloudProductionRequestWireSchema,
  listCloudProductionsQueryWireSchema,
  listCloudProductionsResponseWireSchema,
  stopCloudProductionRequestWireSchema,
} from './cloudProductions.js';
import { deviceSecretPathWireSchema, deviceSecretResponseWireSchema, listDeviceSecretsResponseWireSchema, setDeviceSecretRequestWireSchema } from './deviceSecrets.js';
import type { RouteDef } from './routes.js';
import { studioPathWireSchema } from './studios.js';

export const CLOUD_PRODUCTIONS_ROUTES = {
  launchCloudProduction: {
    id: 'launchCloudProduction',
    method: 'POST',
    path: '/cloud-productions',
    tag: 'cloud-productions',
    summary: 'Launch a cloud production',
    description:
      'Creates a production of a playlist or rundown in a studio of the tenant’s cloud site (§9.1) and runs it in Fargate (§9.2): the package it runs is pinned now (the release the cloud site resolves to, D15), each task gets a short-lived enrolment credential (10 min, in Secrets Manager — never in the task’s definition or environment), and `ecs:RunTask` starts one task, or two in different AZs when `redundant` (§9.3). ' +
      '`startAt: "now"` launches at once; a time schedules the launch 10 minutes before it (EventBridge Scheduler, §9.2.4). The production is `running` once its node enrolled and is ready (§1.1: under 2 minutes). ' +
      'Supplying `productionId` makes a retry idempotent: a cloud production with that id is returned as it is. `validation-failed` for a studio outside the cloud site, a start in the past or too far ahead, or a playlist/rundown that does not exist; `unavailable` when no release is published.',
    params: null,
    query: null,
    body: launchCloudProductionRequestWireSchema,
    response: cloudProductionResponseWireSchema,
    successStatus: 201,
    permission: 'production.launch.cloud',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  listCloudProductions: {
    id: 'listCloudProductions',
    method: 'GET',
    path: '/cloud-productions',
    tag: 'cloud-productions',
    summary: 'List cloud productions',
    description:
      'The tenant’s cloud productions the caller may read, newest first, each with its tasks, status and running cost estimate; and the network they run in — the static egress IPs customers allowlist on their devices (D11) and the task sizes with their prices (§9.6).',
    params: null,
    query: listCloudProductionsQueryWireSchema,
    body: null,
    response: listCloudProductionsResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request'],
  },
  getCloudProduction: {
    id: 'getCloudProduction',
    method: 'GET',
    path: '/cloud-productions/{productionId}',
    tag: 'cloud-productions',
    summary: 'Get a cloud production',
    description: 'One cloud production: its status, its tasks (AZ, private IP, node, start and stop), its pinned package, its deadline and its running cost estimate (§9.6).',
    params: cloudProductionPathWireSchema,
    query: null,
    body: null,
    response: cloudProductionResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  stopCloudProduction: {
    id: 'stopCloudProduction',
    method: 'POST',
    path: '/cloud-productions/{productionId}/stop',
    tag: 'cloud-productions',
    summary: 'Stop a cloud production',
    description:
      'Stops it (§9.2.5): a scheduled one that has not launched is cancelled; a running one drains — its node writes the final as-run and flushes its outbox, then exits — and its tasks are stopped by force if they have not exited within a few minutes. The production record is ended. Idempotent: stopping a stopping or stopped production answers it as it is.',
    params: cloudProductionPathWireSchema,
    query: null,
    body: stopCloudProductionRequestWireSchema,
    response: cloudProductionResponseWireSchema,
    successStatus: 200,
    permission: 'production.launch.cloud',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  getCloudUsage: {
    id: 'getCloudUsage',
    method: 'GET',
    path: '/cloud-usage',
    tag: 'cloud-productions',
    summary: 'Cloud production usage for a month',
    description: 'The tenant’s metered production-hours and task-hours for one month (UTC), with every task run that ended in it and its cost at the on-demand price (§9.6). A run still going is not in it until it stops.',
    params: null,
    query: cloudUsageQueryWireSchema,
    body: null,
    response: cloudUsageResponseWireSchema,
    successStatus: 200,
    permission: 'billing.view',
    permissionScope: 'tenant',
    errors: ['bad-request'],
  },
  setDeviceSecret: {
    id: 'setDeviceSecret',
    method: 'PUT',
    path: '/studios/{studioId}/devices/{deviceId}/secrets',
    tag: 'devices',
    summary: 'Set a device’s secret',
    description:
      'Stores the device’s secret options (an OBS password, an API token) in Secrets Manager, per tenant and device (§9.4, D11), replacing any it had. A production’s node receives them over the site link, sealed to its key, and lays them over the device’s `options` at launch; they are never in rows or the studio config. The answer names the options it holds, never their values. `validation-failed` when the studio config has no such device.',
    params: deviceSecretPathWireSchema,
    query: null,
    body: setDeviceSecretRequestWireSchema,
    response: deviceSecretResponseWireSchema,
    successStatus: 200,
    permission: 'device.configure',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  deleteDeviceSecret: {
    id: 'deleteDeviceSecret',
    method: 'DELETE',
    path: '/studios/{studioId}/devices/{deviceId}/secrets',
    tag: 'devices',
    summary: 'Delete a device’s secret',
    description: 'Deletes the device’s secret for good (no recovery window). A production already running keeps what it was given. `not-found` when the device has none.',
    params: deviceSecretPathWireSchema,
    query: null,
    body: null,
    response: deviceSecretResponseWireSchema,
    successStatus: 200,
    permission: 'device.configure',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  listDeviceSecrets: {
    id: 'listDeviceSecrets',
    method: 'GET',
    path: '/studios/{studioId}/device-secrets',
    tag: 'devices',
    summary: 'List a studio’s device secrets',
    description: 'Which devices of the studio have a secret, and which options each sets — never the values.',
    params: studioPathWireSchema,
    query: null,
    body: null,
    response: listDeviceSecretsResponseWireSchema,
    successStatus: 200,
    permission: 'device.configure',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  getCloudAssignment: {
    id: 'getCloudAssignment',
    method: 'GET',
    path: '/node/cloud-production',
    tag: 'node',
    summary: 'The calling task’s assignment',
    description:
      'For a cloud production’s task (a node of the cloud site, by its signed request): which production it runs and as which role, whether to drain and exit, and — for a redundant pair — its peer and the pair’s secret, sealed to it (§9.2–§9.3). The supervisor polls it. `not-found` for a node that is no cloud production’s task.',
    params: null,
    query: null,
    body: null,
    response: cloudAssignmentResponseWireSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    auth: 'node',
    errors: ['not-found'],
  },
  reportCloudReady: {
    id: 'reportCloudReady',
    method: 'POST',
    path: '/node/cloud-production/ready',
    tag: 'node',
    summary: 'The calling task is ready',
    description:
      'The task’s supervisor says its node is ready (enrolled, its state service up and its site link open, §9.2.3): the main task’s report makes the production `running` and ends the launch → ready clock (§1.1). Idempotent. `validation-failed` when the production is not this node’s.',
    params: null,
    query: null,
    body: cloudReadyRequestWireSchema,
    response: cloudAssignmentResponseWireSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    auth: 'node',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
} as const satisfies Record<string, RouteDef>;
